import styled, {
  SystemProp,
  Theme,
  ThemeSize,
  x
} from '@xstyled/styled-components';
import * as CSS from 'csstype';
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Flex } from '~/ui/flex/Flex';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { IMAGE, LAYOUT_LIST, LAYOUT_COLUMNS } from '../itemIds';
import phoneImage from '~/assets/1x1.png';
import { useEditorStore } from '~/shell/Store';
import { EditBg } from '~/editor/buttons/EditBg';
import { getParentId } from '../helper/getParentId';
import type { ImageGenerationPlacement } from '~/data/imageGenerationPolling';
import { MaybeAnimatedImage } from './MaybeAnimatedImage';

interface Props {
  url: string;
  alt?: string;
  darken?: boolean;
  className?: string;
  borderRadius?: string;
  backgroundSizeForMobile?: string;
  width?: SystemProp<ThemeSize<Theme> | CSS.Property.Width, Theme>;
  height?: SystemProp<ThemeSize<Theme> | CSS.Property.Height, Theme>;
  ariaHidden?: boolean;
  objectFit?: CSS.Property.ObjectFit;
  divWidth?: SystemProp<ThemeSize<Theme> | CSS.Property.Width, Theme>;
  divHeight?: SystemProp<ThemeSize<Theme> | CSS.Property.Height, Theme>;
  editButtonOneTheImage?: boolean;
  editable?: boolean;
  role?: string;
  imageGenerationPlacement?: ImageGenerationPlacement;
  /**
   * Cards6: probe extensionless CDN URLs for GIFs and show pause controls
   * (CAT-1236). Starts paused so animation is off by default.
   */
  enableAnimationPause?: boolean;
}

const DivEl = styled.div<Props>`
  background-image: url(${p => p.url});
  background-position: center;
  background-size: cover;
  flex: 1;
  width: 100%;
  height: ${p => p.height || '100%'};
  filter: brightness(${p => (p.darken ? '0.4' : '1')});
  position: relative;
  background-repeat: no-repeat;
  @media (max-width: sm) {
    background-size: ${p => p.backgroundSizeForMobile || 'cover'};
  }
`;

const ImageEl = styled.imgBox<Partial<Props>>`
  object-fit: contain;
  width: 100%;
  filter: brightness(${p => (p.darken ? '0.4' : '1')});
  border-radius: ${p => p.borderRadius || '0px'};
  pointer-events: none;
`;

export type ImageDefinition = ItemDefinition<typeof IMAGE, Props>;

export class Image extends Item<typeof IMAGE, Props> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected render({
    parent,
    width,
    height,
    editButtonOneTheImage,
    editable = true,
    role = 'img',
    imageGenerationPlacement = 'inline_image',
    ...props
  }: RenderProps<Props>) {
    const { contentEditable } = useEditorStore();
    const divElRef = useRef<HTMLDivElement>(null);
    const imgElRef = useRef<HTMLImageElement>(null);
    const [showPortal, setShowPortal] = useState(false);

    React.useEffect(() => {
      if (divElRef.current) {
        setShowPortal(true);
      }
    }, [divElRef.current]);

    // The image is rendered differently based on the parent type...
    switch (parent?.type) {
      case LAYOUT_COLUMNS:
      case LAYOUT_LIST: {
        const imgProps = {
          darken: props.darken,
          className: props.className,
          borderRadius: props.borderRadius,
          w: width,
          h: height,
          objectFit: props.objectFit
        };

        return (
          <x.div
            position="relative"
            ref={divElRef}
            w={props.divWidth}
            h={props.divHeight}
          >
            <MaybeAnimatedImage
              url={props.url}
              alt={props.alt || ''}
              ariaHidden={props.ariaHidden}
              enableAnimationPause={props.enableAnimationPause}
              renderImg={gifImgProps => (
                <ImageEl {...imgProps} {...gifImgProps} />
              )}
            />
            {contentEditable && editable && (
              <EditBg
                divElRef={divElRef}
                id={props.id}
                type="image"
                currentImage={{
                  id: Number(props.id),
                  mediaUrl: props.url,
                  name: props.alt
                }}
                parentId={getParentId(parent)}
                imageGenerationPlacement={imageGenerationPlacement}
              />
            )}
            <Flex variant="center" position="absolute" top="0" left="0">
              {props.children}
            </Flex>
          </x.div>
        );
      }
      default: {
        /*
          WCAG: `alt` is not valid on a <div>, so the background div itself
          must carry the accessible name. A named background gets
          `role="img"` + `aria-label`; an unnamed one (or a solid-color fill,
          stored with a "#RRGGBB" name) is decorative and is hidden from the
          accessibility tree instead.

          GIF pause controls are only on LAYOUT_LIST/COLUMNS (<img>) paths
          (e.g. Cards6). Full-bleed CSS backgrounds sit under z-index:-1 and
          cannot host a reachable control without a separate HUD solution.
        */
        const { alt, ariaHidden, ...divProps } = props;
        const meaningfulAlt = alt && !alt.includes('#') ? alt : '';
        const isDecorative = Boolean(ariaHidden) || !meaningfulAlt;
        return (
          <>
            <DivEl
              {...divProps}
              height={height}
              role={isDecorative ? 'presentation' : role}
              aria-label={isDecorative ? undefined : meaningfulAlt}
              aria-hidden={isDecorative ? true : undefined}
              ref={divElRef}
            />
            {contentEditable && editable && editButtonOneTheImage ? (
              <>
                <x.img
                  ref={imgElRef}
                  src={phoneImage}
                  w="1px"
                  h="1px"
                  position="absolute"
                  alt=""
                  aria-hidden={true}
                  className="bg-wrapper"
                />
                <EditBg
                  divElRef={divElRef}
                  id={props.id}
                  type="image"
                  currentImage={{
                    id: Number(props.id),
                    mediaUrl: props.url,
                    name: props.alt
                  }}
                  imageGenerationPlacement={imageGenerationPlacement}
                />
              </>
            ) : (
              <x.img
                src={phoneImage}
                w="1px"
                h="1px"
                position="absolute"
                alt=""
                aria-hidden={true}
                className="bg-wrapper"
              />
            )}

            {contentEditable &&
              editable &&
              !editButtonOneTheImage &&
              showPortal &&
              createPortal(
                <EditBg
                  divElRef={divElRef}
                  id={props.id}
                  type="image"
                  currentImage={{
                    id: Number(props.id),
                    mediaUrl: props.url,
                    name: props.alt
                  }}
                  imageGenerationPlacement={imageGenerationPlacement}
                />,
                document.body
              )}
          </>
        );
      }
    }
  }
}

registry[IMAGE] = Image;
